/**
 * Terrain meshing (PRD §11): 3-D geometry generated by marching squares over the
 * density field, extruded in Z so cliff faces have real thickness and parallax.
 * Collision stays analytic against the same field, so it is simple and forgiving.
 *
 * Fully solid cells are merged into runs before triangulation, which keeps a
 * 400 m-deep chunk down to a few thousand triangles instead of a few hundred
 * thousand.
 *
 * Everything in this file is pure: field in, vertex and index arrays out. No
 * GPU, no DOM, nothing that touches the running game. That is what lets the
 * whole thing run inside a worker — see `meshWorker.ts` — so that meshing a
 * chunk costs the frame nothing at all.
 */

import { density, detailBand, featuresFor, seabedY, type Feature } from './field';
import { WORLD } from './config';
import { VERT_FLOATS, type MeshData } from '../render/mesh';

export interface LayerSpec {
  /** Front / back Z of the extrusion. */
  zFront: number;
  /**
   * How far out of focus this layer is, 0 sharp to 1 fully soft.
   *
   * Depth of field asks the layers rather than reconstructing distance from
   * the depth buffer, because the layers are the answer: the play layer holds
   * everything that can be shot, collided with or collected, and the parallax
   * layers hold everything that cannot. Reconstruction got that wrong in both
   * directions — it blurred a jellyfish hanging in open water because the
   * translucent pass writes no depth, and it had no way to know that a slab
   * fifty metres behind the boat is scenery rather than an obstacle.
   */
  defocus: number;
  zBack: number;
  /** Cell size multiplier — background layers are coarser. */
  cellMul: number;
  /** Horizontal warp so background silhouettes don't mirror the play layer. */
  warp: number;
  /**
   * How far above the *local* seabed this layer sits. Not an absolute shift —
   * see `layerLift`.
   */
  yOffset: number;
  tint: [number, number, number];
}

/**
 * The tints are the depth cue, and they are not subtle on purpose. Three
 * planes of rock lit by the same ambient come back at almost the same value
 * however far apart they are, because extinction over 90 m of water is a small
 * number — so the frame flattens into one grey field with different silhouettes
 * cut out of it. Taking the albedo down hard on the far layers turns them into
 * what distance actually looks like underwater: shapes, not surfaces. The hue
 * still cools with distance, which is the other half of aerial perspective;
 * what changed is that there is now something to cool.
 */
export const LAYERS: LayerSpec[] = [
  { zFront: 3.2, zBack: -16, cellMul: 1, warp: 0, yOffset: 0, tint: [1, 1, 1], defocus: 0 },
  { zFront: -52, zBack: -96, cellMul: 2.4, warp: 431.7, yOffset: 26, tint: [0.46, 0.55, 0.66], defocus: 0.55 },
  { zFront: -138, zBack: -190, cellMul: 3.8, warp: 1907.3, yOffset: 54, tint: [0.20, 0.28, 0.40], defocus: 1 },
];

/**
 * Where a parallax layer sits vertically at `x`.
 *
 * The layers warp their x so the background is not a mirror of the play layer,
 * and a fixed `yOffset` on top of that works exactly as long as the authored
 * profile keeps descending. It does not. The kelp forest deliberately climbs
 * back to about -100 m while the ground on either side has fallen past -260, so
 * for a player in the trench at x 2690 the layer samples x 3122, lands on the
 * shelf, and hangs it 223 m above his head — a rock ridge in open water with
 * weed growing on it, which is precisely what it looked like.
 *
 * So the offset is measured from the local seabed instead. The warp still
 * supplies the silhouette — the far column's *features* come across intact,
 * which is the whole point of warping — but its profile height does not: the
 * ridge is re-anchored to sit `yOffset` above the ground the player is actually
 * over. The background can no longer detach from the world in front of it.
 *
 * `props.ts` seats background growth through this same function, so the weed
 * and the rock it grows on cannot disagree.
 */
export function layerLift(x: number, L: LayerSpec): number {
  if (L.warp === 0) return L.yOffset;
  const lift = seabedY(x) - seabedY(x + L.warp) + L.yOffset;
  // And never above the water.
  //
  // Re-anchoring to the local seabed fixed a background that detached
  // downward; it does nothing about one that detaches UPWARD. Where the warped
  // column is much deeper than the local one the lift goes strongly positive,
  // and measured over the world layer 2 peaked at +31 m and layer 1 at +3.2 m —
  // background ridges standing out of the sea, in the shipped game, a few
  // hundred metres from the spawn.
  //
  // This is the third instance of one bug class: enemy spawn heights, the
  // trench's floating kelp shelf, and now this. All three came from assuming a
  // relationship between two columns of a deliberately non-monotonic seabed.
  // `props.ts` seats background growth through this same function, so the weed
  // follows the rock rather than needing its own fix.
  return Math.min(lift, WORLD.surfaceY - 6 - seabedY(x + L.warp));
}

/**
 * The highest a body may float on this lane, in DRAWN y.
 *
 * There is no water surface mesh in this game. The waterline a player sees is
 * simply where the terrain strip stops and nothing is drawn, so the only
 * honest ceiling for anything that swims is the top of the strip its own lane
 * is meshed to — the two numbers below are lifted straight out of `meshLayer`
 * and `density`, and if either moves this has to move with it.
 *
 * WHY THIS IS NOT `surfaceY + layerLift`, which is what the enemy clamp used:
 * `layerLift` is capped so a background layer's SEABED stays under water, and
 * that cap is `surfaceY - 6 - seabedY`. Over a deep warped column the seabed is
 * far down, so the cap is far UP — measured over the world it lets a lift run
 * to tens of metres. `surfaceY + lift` is then a ceiling well above the sea,
 * while `meshLayer` independently clamps that same lane's rock to `surfaceY-2`.
 * Two rules, one lane, disagreeing: the rock stopped at the waterline and the
 * creatures kept going, so they ended up hanging in the empty sky above it,
 * shoved back down by the clamp and pushed up again by their own steering,
 * every frame. That fight is the convulsing.
 */
export function laneCeiling(x: number, L: LayerSpec): number {
  // `density` admits no rock above this in FIELD y; the lift takes it to drawn.
  const top = WORLD.surfaceY - 1.5 + layerLift(x, L);
  // And background lanes are clamped a second time, by the strip itself.
  return L.warp === 0 ? top : Math.min(top, WORLD.surfaceY - 2);
}

interface PolyPoint {
  x: number;
  y: number;
  iso: boolean;
  d: number;
}

/**
 * Metres from the rock surface at which the shade hint saturates.
 *
 * METRES now, because the hint is a distance now. Thirty-four of them, which
 * is how far light is allowed to reach into stone before the rock is simply
 * dark — the reach is this number and nothing else. It used to be a raw density
 * value, and density is not a distance: its magnitude is whatever the authored
 * features happen to add, so it stepped by up to 135 across a feature's
 * bounding box — the whole 0..1 range in no distance at all, which is the
 * rectangle that has been drawn around every cave.
 */
const SHADE_DEPTH = 45;

/**
 * Exponent of the ao falloff, substituted into `scene.wgsl` so the number a
 * player sees has one home. Higher is softer: it keeps the value near the top
 * of the range for longer, so light lingers further into stone and then goes.
 *
 * THIS is the dial for softness, not `SHADE_DEPTH`. The exponent is free —
 * it changes nothing about the mesh. Reach costs triangles, because a longer
 * gradient means less rock is flat enough to merge, and past about 45 m it
 * costs correctness too: the hint is `d / |grad d|`, an ESTIMATE, and it is
 * clamped at 60 m. Take the reach to the clamp and nothing saturates any more,
 * so the estimator's own disagreement between neighbouring chunks stops being
 * hidden — measured, a 0.126 seam in the hint where 45 m gives 0.024.
 */
export const AO_EXPONENT = 3.6;

/**
 * How close two runs have to be for one to swallow the other.
 *
 * In SHADE units, and deliberately below the shade change across one cell of
 * the gradient — 0.62 m over `SHADE_DEPTH` — so the gradient itself never
 * merges and only genuinely flat interior does.
 *
 * Judging this on brightness instead was tried and is wrong, which is worth
 * recording because it sounds better: the eased curve is nearly FLAT wherever
 * it is bright, so a run passes the test across a huge span of the hint, and
 * then meets a cell where the curve has steepened. Measured, that put a 0.30
 * brightness seam on the mesh — thirty per cent of the range between lit and
 * black, against a tolerance of one. The tolerance has to bound the quantity
 * the vertices actually interpolate, which is the hint.
 */
const MERGE_EPS = 0.012;

/**
 * Radius, IN METRES, of the blur applied to the shade hint.
 *
 * It used to be a radius in cells — `BLUR = 3` — with a comment claiming that
 * was "about nine metres of ground at the play layer's resolution". It is not:
 * `WORLD.cell` is 0.62 m, so three cells is 1.86 m, and the blur was running
 * five times narrower than the number it was tuned to. The step it exists to
 * hide is a feature's bounding box, where the field jumps by up to 135 against
 * a `SHADE_DEPTH` of 14 — the whole 0..1 shade range in one sample — so at
 * 1.86 m it smoothed the edge into a slightly softer edge and no further.
 *
 * As metres it also holds across layers, which cells never did: the same three
 * cells were 1.86 m on the play layer and 7.1 m on the far parallax, so the one
 * place it nearly worked was the one place its own comment was accidentally
 * right about.
 *
 * Long enough that a one-sample step becomes a gradient the eye reads as light
 * falling off; short enough that real relief keeps its own shading.
 */
const BLUR_METRES = 1.86;

/**
 * Kernel radius in cells.
 *
 * Back to three cells at the play layer, which is what shipped for months and
 * what the gentle bleed of light into stone was tuned against. Widening it to
 * nine metres removed the box edges and took that bleed with it: a symmetric
 * average that reaches nine metres into rock reads half-deep everywhere within
 * nine metres of a wall, so the hint saturates almost at the surface. The step
 * comes out of the field now, not out of the gradient beside it.
 */
const blurCells = (cellWidth: number) =>
  Math.max(2, Math.min(16, Math.round(BLUR_METRES / cellWidth)));

/**
 * How many y-slices a chunk layer's index buffer is divided into, for the
 * renderer's vertical cull. Marching squares already walks the strip one row of
 * cells at a time, bottom to top, so the indices come out sorted in y for free
 * — all this costs is remembering where each band started.
 *
 * 48 is well past the point of diminishing returns: at the tallest band in the
 * world (the trench, ~530 m) a slice is 11 m, which is a sixth of what the near
 * layer can show at once.
 */
const SLICES = 96;

/*
 * Vertical resolution was briefly relaxed on tall bands to cut meshing cost.
 * It worked, and it was visible: marching squares interpolates the crossing so
 * the error is second-order, but the *shading* is not interpolated — the
 * per-vertex depth-shade hint quantises with the grid, and coarser rows banded
 * the rock. Reverted. The grid stays uniform; the cost is being solved by
 * moving meshing off the frame thread instead of by spending fidelity.
 */

/** Shade hint packed into uv.y: 0 = freshly exposed rock, 1 = deep interior. */
const depthShade = (d: number) => Math.min(1, Math.max(0, d / SHADE_DEPTH));

function meshStrip(
  x0: number,
  x1: number,
  lo: number,
  hi: number,
  cellX: number,
  cellY: number,
  sample: (x: number, y: number, feats: Feature[]) => number,
  feats: Feature[],
  zFront: number,
  zBack: number,
  /** Fill everything from here up to `lo` with one solid quad, or null. */
  fillFrom: number | null,
  /**
   * A column's x, mapped into the space `feats` are expressed in.
   *
   * The per-column filter below narrows the feature list to the ones that can
   * touch this column, and it was comparing the two in different coordinate
   * spaces. A parallax layer samples the field at `x + warp`, so its features
   * come back spanning 476..532 while its columns run 48..96 — the predicate is
   * then false for every feature of every column, `colFeats` is empty, and the
   * whole background is meshed from the base seabed noise with no arches, no
   * boulders and no overhangs in it.
   *
   * Nothing looked broken, because you cannot miss a silhouette you have never
   * seen. What gave it away was seagrass: props are seated with `floorAt`,
   * which reads the real field, so a blade would take root on the top of a
   * feature that the background mesh did not contain and stand in open water
   * with nothing under it. The play layer was always fine — its warp is 0, so
   * the two spaces coincide and the predicate happened to be correct.
   */
  featX: (x: number) => number = (x) => x,
): MeshData {
  const cols = Math.max(1, Math.round((x1 - x0) / cellX));
  const cw = (x1 - x0) / cols;
  const rows = Math.max(1, Math.round((hi - lo) / cellY));
  const ch = (hi - lo) / rows;

  // Sample grid, one column at a time so the field's per-column caches stay hot.
  //
  // The margin is sampled but never meshed. It exists so the blur below is the
  // same function on both sides of a chunk seam: a kernel that clamped at the
  // chunk edge would produce two different answers for the same piece of rock
  // depending on which chunk was asking, and put a seam exactly where this is
  // trying to remove one.
  // The margin is in X ONLY, and it is the reason this costs anything at all:
  // those columns are sampled from the field and never meshed. They buy seam
  // consistency, because a blur kernel that clamped at a chunk edge would give
  // two different answers for the same rock depending on which chunk asked. In
  // Y there is no such neighbour — a chunk's band is its own — so the kernel
  // clamps there for free. Six extra columns instead of six extra columns AND
  // six extra rows is most of the cost back.
  const stride = rows + 1;
  const BLUR = blurCells(cw);
  const g = new Float32Array((cols + 1 + 2 * BLUR) * stride);
  for (let i = -BLUR; i <= cols + BLUR; i++) {
    const x = x0 + i * cw;
    const fx = featX(x);
    const colFeats = feats.filter((f) => fx >= f.x0 - cw && fx <= f.x1 + cw);
    for (let j = 0; j <= rows; j++) {
      g[(i + BLUR) * stride + j] = sample(x, lo + j * ch, colFeats);
    }
  }
  /** Grid accessor in un-margined coordinates. */
  const G = (i: number, j: number) => g[(i + BLUR) * stride + j];

  // HOW FAR FROM THE SURFACE, NOT HOW DENSE.
  //
  // `d / |grad d|` is the first-order distance to the zero crossing, and it is
  // the whole fix. Density is not a distance: an authored feature adds whatever
  // it adds, so the value jumps by up to 135 across a bounding box while the
  // real surface stays where it was. The gradient jumps in the same place and
  // by the same cause, so the RATIO barely moves — the box stops being visible
  // without anything being smoothed away.
  //
  // Free: the derivatives are central differences on a grid that already
  // exists, and the margin already carries the neighbours they need.
  const GD = (i: number, j: number) => {
    const jl = Math.max(0, j - 1), jh = Math.min(rows, j + 1);
    const gx = (G(i + 1, j)! - G(i - 1, j)!) / (2 * cw);
    const gy = (G(i, jh)! - G(i, jl)!) / ((jh - jl) * ch || ch);
    // Floored so flat interior cannot divide to infinity, and clamped so one
    // sample cannot drag the blurred average with it.
    const v = G(i, j)! / Math.max(Math.hypot(gx, gy), 0.05);
    return Number.isFinite(v) ? Math.max(-60, Math.min(60, v)) : 60;
  };

  // SHADING READS A BLURRED COPY. THE SURFACE DOES NOT.
  //
  // `depthShade` asks how deep inside the rock a corner is, and the answer used
  // to come straight off this grid — which steps hard wherever an authored
  // feature's bounding box ends, because a feature is skipped outside its box
  // and its contribution is still live at the edge. Measured at the chamber at
  // 1492: density 6 one sample and 45 the next. Cells above `SHADE_DEPTH` are
  // then merged into one saturated quad and cells below are lit individually,
  // so a difference of one sample became a black rectangle with a straight
  // edge, which is what a cave looked like.
  //
  // Fixing the field itself is the obvious move and it is not available: that
  // truncation turns out to be load-bearing — chambers carve well past their
  // ellipse because of it, and the audit reports the octopus arena walled off
  // and a vault sealed in rock the moment it is smoothed. So the STEP is
  // removed where it does damage, in the shading, and the field keeps the shape
  // the level was tuned against. Collision, the contour and every golden trace
  // read `G` exactly as before; only the number packed into uv.y is blurred.
  const gs = new Float32Array((cols + 1) * stride);
  {
    const tmp = new Float32Array((cols + 1) * stride);
    const w = 1 / (BLUR * 2 + 1);
    // A RUNNING SUM, so the radius is free. The old kernel re-added every tap
    // for every cell, which is what made a wide blur look expensive and kept
    // this at three cells; sliding the window costs one add and one subtract
    // per cell no matter how wide it is.
    for (let j = 0; j <= rows; j++) {
      let sum = 0;
      for (let k = -BLUR; k <= BLUR; k++) sum += GD(k, j);
      for (let i = 0; i <= cols; i++) {
        tmp[i * stride + j] = sum * w;
        // The margin holds real samples out to cols + BLUR, so no clamp here.
        if (i < cols) sum += GD(i + 1 + BLUR, j) - GD(i - BLUR, j);
      }
    }
    for (let i = 0; i <= cols; i++) {
      const base = i * stride;
      const at = (j: number) => tmp[base + Math.min(rows, Math.max(0, j))]!;
      let sum = 0;
      for (let k = -BLUR; k <= BLUR; k++) sum += at(k);
      for (let j = 0; j <= rows; j++) {
        gs[base + j] = sum * w;
        // Y has no margin — a chunk's band is its own — so the ends clamp.
        if (j < rows) sum += at(j + 1 + BLUR) - at(j - BLUR);
      }
    }
  }
  /** Blurred grid accessor, for the shade hint only. */
  const S = (i: number, j: number) => gs[Math.min(cols, i) * stride + j];

  // Growable typed arrays rather than `number[]`. A cave chunk emits tens of
  // thousands of vertices, and nine `Array.push` arguments per vertex was
  // costing more than the field sampling that produced them.
  let verts = new Float32Array(4096 * VERT_FLOATS);
  let vn = 0;
  let idx = new Uint32Array(8192);
  let inn = 0;

  /**
   * `up` is HOW MUCH THIS SURFACE FACES THE SKY, 0 to 1, and it goes in the
   * ninth float — which was already there, already written, and always zero.
   *
   * The cap needs it because its vertex normal cannot carry it. The cap is the
   * flat front of the extrusion, so every one of its normals is (0, 0, 1): a
   * shader asking "is this ground" got `nrm.y == 0` on the entire surface that
   * fills the screen, and no sediment could ever land there. The information
   * exists here and nowhere else — the density field's gradient is the real
   * landscape's surface normal, and this is the only place that field is read.
   */
  const V = (x: number, y: number, z: number, nx: number, ny: number, nz: number,
             u: number, v: number, up = 0) => {
    if (vn + VERT_FLOATS > verts.length) {
      const next = new Float32Array(verts.length * 2);
      next.set(verts);
      verts = next;
    }
    verts[vn] = x; verts[vn + 1] = y; verts[vn + 2] = z;
    verts[vn + 3] = nx; verts[vn + 4] = ny; verts[vn + 5] = nz;
    verts[vn + 6] = u; verts[vn + 7] = v; verts[vn + 8] = up;
    vn += VERT_FLOATS;
    return vn / VERT_FLOATS - 1;
  };
  const I6 = (a: number, b: number, c: number, d: number, e: number, f: number) => {
    if (inn + 6 > idx.length) {
      const next = new Uint32Array(idx.length * 2);
      next.set(idx);
      idx = next;
    }
    idx[inn] = a; idx[inn + 1] = b; idx[inn + 2] = c;
    idx[inn + 3] = d; idx[inn + 4] = e; idx[inn + 5] = f;
    inn += 6;
  };
  const I3 = (a: number, b: number, c: number) => {
    if (inn + 3 > idx.length) {
      const next = new Uint32Array(idx.length * 2);
      next.set(idx);
      idx = next;
    }
    idx[inn] = a; idx[inn + 1] = b; idx[inn + 2] = c;
    inn += 3;
  };
  const capQuad = (
    ax: number, ay: number, bx: number, by: number,
    d00: number, d10: number, d11: number, d01: number,
  ) => {
    // THE DENSITY GRADIENT IS THE LANDSCAPE'S SURFACE NORMAL, and the four
    // corners of this cell are four samples of that field. Higher density is
    // deeper into rock — see `depthShade` — so the outward normal points down
    // the gradient, and a surface faces the sky exactly when the rock thins out
    // as you go up.
    //
    // Per quad rather than per vertex: the cell is one plane of the field and
    // the four corners are what define it.
    const gx = (d10 + d11) - (d00 + d01);
    const gy = (d01 + d11) - (d00 + d10);
    const gl = Math.hypot(gx, gy);
    // Flat cells — the solid interior, where every corner reads the same — have
    // no gradient and no orientation. Zero is right: the inside of a rock is
    // not ground, and it is also never seen.
    const up = gl > 1e-6 ? Math.max(0, -gy / gl) : 0;
    const a = V(ax, ay, zFront, 0, 0, 1, 0, depthShade(d00), up);
    const b = V(bx, ay, zFront, 0, 0, 1, 0, depthShade(d10), up);
    const c = V(bx, by, zFront, 0, 0, 1, 0, depthShade(d11), up);
    const d = V(ax, by, zFront, 0, 0, 1, 0, depthShade(d01), up);
    I6(a, b, c, a, c, d);
  };
  const wall = (ax: number, ay: number, bx: number, by: number) => {
    let nx = by - ay, ny = -(bx - ax);
    const l = Math.hypot(nx, ny) || 1;
    nx /= l; ny /= l;
    const a = V(ax, ay, zFront, nx, ny, 0, 1, 0);
    const b = V(bx, by, zFront, nx, ny, 0, 1, 0);
    const c = V(bx, by, zBack, nx, ny, 0, 1, 1);
    const d = V(ax, ay, zBack, nx, ny, 0, 1, 1);
    I6(a, b, c, a, c, d);
  };

  const poly: PolyPoint[] = [];
  const cx = [0, 0, 0, 0];
  const cy = [0, 0, 0, 0];
  const cv = [0, 0, 0, 0];
  /** The same four corners on the BLURRED grid. Shading only; never the contour. */
  const sv = [0, 0, 0, 0];

  // Slice table. `sliceY[k]` is the bottom of slice k and `sliceStart[k]` the
  // first index belonging to it, so the renderer can draw the sub-range that
  // covers the camera's y band and nothing else.
  const rowsPerSlice = Math.max(1, Math.ceil(rows / SLICES));
  const maxSlices = Math.ceil(rows / rowsPerSlice) + 2;
  const sliceY = new Float32Array(maxSlices + 1);
  const sliceStart = new Uint32Array(maxSlices + 1);
  let sl = 0;

  // Solid fill below the detail band — one quad instead of thousands of cells.
  // Emitted first because it is the lowest geometry in the strip, and the slice
  // table is only useful if the index buffer stays sorted in y.
  if (fillFrom !== null) {
    sliceY[sl] = fillFrom;
    sliceStart[sl] = 0;
    sl++;
    const a = V(x0, fillFrom, zFront, 0, 0, 1, 0, 1);
    const b = V(x1, fillFrom, zFront, 0, 0, 1, 0, 1);
    const c = V(x1, lo, zFront, 0, 0, 1, 0, 1);
    const d = V(x0, lo, zFront, 0, 0, 1, 0, 1);
    I6(a, b, c, a, c, d);
  }

  for (let j = 0; j < rows; j++) {
    const ya = lo + j * ch;
    const yb = ya + ch;
    if (j % rowsPerSlice === 0) {
      sliceY[sl] = ya;
      sliceStart[sl] = inn;
      sl++;
    }
    let runStart = -1;
    let runShade = 0;
    for (let i = 0; i <= cols; i++) {
      const v00 = i < cols ? G(i, j) : 0;
      const v10 = i < cols ? G(i + 1, j) : 0;
      const v11 = i < cols ? G(i + 1, j + 1) : 0;
      const v01 = i < cols ? G(i, j + 1) : 0;
      // Shading corners come off the blurred grid; the contour never does.
      const s00 = i < cols ? S(i, j) : 0;
      const s10 = i < cols ? S(i + 1, j) : 0;
      const s11 = i < cols ? S(i + 1, j + 1) : 0;
      const s01 = i < cols ? S(i, j + 1) : 0;

      // MERGE WHAT IS FLAT, NOT WHAT IS SATURATED.
      //
      // It used to require all four corners past `SHADE_DEPTH`, which tied the
      // biggest triangle saving in the mesher to the shade's saturation point —
      // so pushing light further into the rock stopped the merging and cost 70 %
      // more terrain triangles for a change that was only meant to be a look.
      //
      // What the merge actually needs is that the run is FLAT: if every corner
      // it covers agrees to within a shade step nobody can see, painting the
      // whole span with one value is free. Deep interior is flat whatever its
      // absolute value, which is where the triangles were all along. `solid` is
      // still the real field — a merged run must never cover a cell the surface
      // passes through.
      const solid = i < cols && v00 > 0 && v10 > 0 && v11 > 0 && v01 > 0;
      const sh = (v: number) => (v > SHADE_DEPTH ? 1 : v < 0 ? 0 : v / SHADE_DEPTH);
      const c0 = sh(s00), c1 = sh(s10), c2 = sh(s11), c3 = sh(s01);
      const flat = solid
        && Math.abs(c1 - c0) < MERGE_EPS && Math.abs(c2 - c0) < MERGE_EPS
        && Math.abs(c3 - c0) < MERGE_EPS
        && (runStart < 0 || Math.abs(c0 - runShade) < MERGE_EPS);

      if (flat) {
        if (runStart < 0) { runStart = i; runShade = c0; }
        continue;
      }
      if (runStart >= 0) {
        const d = runShade * SHADE_DEPTH;
        capQuad(x0 + runStart * cw, ya, x0 + i * cw, yb, d, d, d, d);
        runStart = -1;
      }
      if (i >= cols) break;

      if (solid) {
        // Solid but still within the shaded band — its own quad, correctly lit.
        capQuad(x0 + i * cw, ya, x0 + (i + 1) * cw, yb, s00, s10, s11, s01);
        continue;
      }
      if (v00 <= 0 && v10 <= 0 && v11 <= 0 && v01 <= 0) continue;

      const xa = x0 + i * cw, xb = xa + cw;
      cx[0] = xa; cy[0] = ya; cv[0] = v00;
      cx[1] = xb; cy[1] = ya; cv[1] = v10;
      cx[2] = xb; cy[2] = yb; cv[2] = v11;
      cx[3] = xa; cy[3] = yb; cv[3] = v01;
      sv[0] = s00; sv[1] = s10; sv[2] = s11; sv[3] = s01;

      poly.length = 0;
      const saddle =
        (v00 > 0 && v11 > 0 && v10 <= 0 && v01 <= 0) ||
        (v10 > 0 && v01 > 0 && v00 <= 0 && v11 <= 0);
      const centre = (v00 + v10 + v11 + v01) * 0.25;

      for (let k = 0; k < 4; k++) {
        const k2 = (k + 1) & 3;
        const pIn = cv[k] > 0;
        const qIn = cv[k2] > 0;
        // SHADE FROM THE BLURRED GRID, LIKE EVERY OTHER QUAD IN THE STRIP.
        //
        // This was the one emission path the shade-hint blur never reached. A
        // solid cell writes `S` at its corners and a merged run writes the
        // saturated constant, but the marching-squares cap wrote the RAW
        // density at the SAME shared corner — so two different numbers met
        // along an axis-aligned cell edge and the shade jumped across it with
        // no width at all. Where a merged run (shade 1, black) touches a
        // marching cell one cell inside the rock (raw shade ~0.05, lit) that is
        // the full range in one step, and the lit/dark boundary becomes a
        // staircase whose tread is exactly one cell.
        //
        // It reads worst on the parallax layers and that is not a coincidence:
        // their cells are 2.4x and 3.8x wider, so both the step and the
        // disagreement grow with them — `BLUR` is a radius in CELLS, so the
        // blurred value on layer 2 averages 7 m of rock where the raw value is
        // a point sample. The contour itself still comes off `cv`; only the
        // shade moves.
        if (pIn) poly.push({ x: cx[k], y: cy[k], iso: false, d: sv[k] });
        if (pIn !== qIn) {
          const t = cv[k] / (cv[k] - cv[k2]);
          poly.push({
            x: cx[k] + (cx[k2] - cx[k]) * t,
            y: cy[k] + (cy[k2] - cy[k]) * t,
            iso: true,
            d: 0,
          });
        }
      }

      if (saddle && centre <= 0) {
        // Two disconnected corners — emit them as separate triangles.
        emitTri(poly.slice(0, 3));
        emitTri(poly.slice(3, 6));
      } else {
        emitFan(poly);
      }
    }
  }

  function signedArea(p: PolyPoint[]): number {
    let a = 0;
    for (let k = 0; k < p.length; k++) {
      const q = p[(k + 1) % p.length];
      a += p[k].x * q.y - q.x * p[k].y;
    }
    return a * 0.5;
  }

  function emitPoly(p: PolyPoint[]) {
    if (p.length < 3) return;
    if (signedArea(p) < 0) p.reverse();
    const base = vn / VERT_FLOATS;
    for (const pt of p) V(pt.x, pt.y, zFront, 0, 0, 1, 0, depthShade(pt.d));
    for (let k = 1; k + 1 < p.length; k++) I3(base, base + k, base + k + 1);
    for (let k = 0; k < p.length; k++) {
      const q = p[(k + 1) % p.length];
      if (p[k].iso && q.iso) wall(p[k].x, p[k].y, q.x, q.y);
    }
  }
  function emitFan(p: PolyPoint[]) { emitPoly(p); }
  function emitTri(p: PolyPoint[]) { emitPoly(p); }

  sliceY[sl] = hi;
  sliceStart[sl] = inn;

  return {
    vertices: verts.subarray(0, vn),
    indices: idx.subarray(0, inn),
    sliceY: sliceY.subarray(0, sl + 1),
    sliceStart: sliceStart.subarray(0, sl + 1),
  };
}


/**
 * Mesh one layer of one chunk. Deterministic from the field alone, so the same
 * arguments always produce the same buffers whether this runs on the main
 * thread or in a worker.
 */
export function meshChunkLayer(index: number, li: number, occupied = 0): MeshData {
  const x0 = index * WORLD.chunkSize;
  const x1 = x0 + WORLD.chunkSize;
  const L = LAYERS[li];
  /**
   * The lane the sub is flying gets meshed like the play lane, whichever slab
   * it happens to be.
   *
   * `cellMul` is a distance cue, not a property of the ground: layer 1 is the
   * real seabed 431.7 m along, and 2.4x cells are the right way to draw it
   * from 60 m away and the wrong way to draw it from inside. Collision samples
   * the analytic field either way, so a lane left coarse is one where the rock
   * you hit is not the rock you can see.
   */
  const here = li === occupied;
  const cell = WORLD.cell * (here ? 1 : L.cellMul);
  const wx0 = x0 + L.warp, wx1 = x1 + L.warp;
  const feats = featuresFor(wx0 - 4, wx1 + 4);
  const band = detailBand(wx0, wx1);
  // The lift varies across the chunk now, so the band has to cover its whole
  // range or the strip gets clipped where the ground is steepest.
  let liftLo = Infinity, liftHi = -Infinity;
  for (let i = 0; i <= 8; i++) {
    const l = layerLift(x0 + ((x1 - x0) * i) / 8, L);
    if (l < liftLo) liftLo = l;
    if (l > liftHi) liftHi = l;
  }
  const lo = Math.floor((band.lo + liftLo) / cell) * cell;
  let hi = Math.ceil((band.hi + liftHi) / cell) * cell;
  // Nothing in the background may stand out of the sea.
  //
  // `layerLift` already clamps so the layer's *seabed* stays under water, and
  // that was enough while the parallax layers were meshed with no features in
  // them. With features restored, an outcrop standing 10 m proud of a seabed
  // sitting 6 m down puts rock 4 m into the air — measured at 61 columns, up to
  // +9.6 m. Clamping the strip is both cheaper and more honest than deepening
  // the lift: rock meeting the waterline is a coastline, and `layerLift` runs
  // once per grid sample, so it is the last place to put a feature query.
  // ...except in the lane being flown, where the strip has to be a faithful
  // contour or collision finds rock that was never drawn. The clamp exists so
  // background rock never stands out of the sea; the lane under the camera is
  // not background, and `density` has its own waterline clamp at surfaceY-1.5.
  if (L.warp !== 0 && !here) hi = Math.min(hi, WORLD.surfaceY - 2);

  const sample = (x: number, y: number, f: Feature[]) => density(x + L.warp, y - layerLift(x, L), f);
  return meshStrip(
    x0, x1, lo, hi, cell, cell, sample, feats, L.zFront, L.zBack,
    lo > WORLD.yMin + 0.5 ? WORLD.yMin : null,
    (x) => x + L.warp,
  );
}

/** How out of focus a prop on layer `i` is. Props carry an index, not a spec. */
export function layerDefocus(i: number): number {
  return LAYERS[Math.min(i, LAYERS.length - 1)]?.defocus ?? 1;
}

// ---------------------------------------------------------------------------
// Lanes
// ---------------------------------------------------------------------------

/**
 * Where a point DRAWN on layer `li` actually lives in the field.
 *
 * This is `meshChunkLayer`'s own `sample` inverted out of the mesher and given
 * a name, because it is the whole reason a background layer can be flown into
 * rather than merely looked at. A parallax layer is not scenery: `cellMul` is
 * the marching-squares cell size and `warp` is a real horizontal offset, so
 * layer 1 IS the seabed 431.7 m along, drawn coarsely and re-anchored per
 * column. Sample the field through here and a lane collides exactly as it
 * looks.
 */
export function toField(
  li: number, x: number, y: number, out: { x: number; y: number },
): { x: number; y: number } {
  const L = LAYERS[Math.max(0, Math.min(LAYERS.length - 1, li))]!;
  out.x = x + L.warp;
  out.y = y - layerLift(x, L);
  return out;
}

/**
 * Where a sub flying lane `li` sits, as an offset from that layer's FRONT face.
 *
 * Not the middle of the slab, which is what this was and which quietly moved
 * the whole game: lane 0 spans +3.2 to -16, so its centre is -6.4, and the sub
 * has sat at 0 for the entire life of the game. Everything downstream followed
 * it back — the camera focus, and `lightZ`, which feeds a spot light and a
 * volumetric march that are both true 3-D solves, so the searchlight visibly
 * changed shape in open water for reasons that had nothing to do with lanes.
 *
 * Anchoring to the front face keeps lane 0 at exactly 0 and puts the sub the
 * same distance into every other lane, which is the property actually wanted.
 */
export function laneZ(li: number): number {
  const L = LAYERS[Math.max(0, Math.min(LAYERS.length - 1, li))]!;
  return L.zFront - LAYERS[0]!.zFront;
}

/**
 * How much of a layer to draw, 1 down to 0, from where the camera is.
 *
 * A layer between the camera and the sub is a wall in the way, and in the
 * hindmost lane the camera sits INSIDE layer 1's slab — 36 m of rock between
 * the eye and the thing it is looking at. That, not "the world is behind me",
 * was what made the far lane unusable, and this is the ordinary third-person
 * answer to it rather than a lane special case: geometry the camera has
 * reached is not drawn.
 *
 * It fades over the last few metres so a slab dissolves instead of blinking
 * out, and the fade is crossed while the sub is in the open water between
 * lanes, where there is nothing to notice it against. Terrain and the props
 * standing on it both ask this, so weed can never be left hanging in front of
 * ground that is no longer there.
 */
export function layerVisibility(camZ: number, zFront: number): number {
  const FADE = 14;
  return Math.max(0, Math.min(1, (camZ - (zFront + 2)) / FADE));
}

/**
 * Which lane a given z is inside, or -1 for the open water between them.
 *
 * Collision needs no transit state machine because of this: the sub collides
 * with whatever slab it is currently within, and the ~36 m gaps between the
 * slabs genuinely are open water, so returning -1 there is not a special case
 * being tolerated — it is the honest answer.
 */
export function laneAtZ(z: number): number {
  for (let i = 0; i < LAYERS.length; i++) {
    const L = LAYERS[i]!;
    if (z <= L.zFront && z >= L.zBack) return i;
  }
  return -1;
}
